import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { lazy, Suspense, useContext, useEffect } from 'react';

// Contexts & Providers (not lazy-loaded)
import { AuthContext } from '../context/authContext';
import CalcIneligibleContextProvider from '../context/calneligibleContext';
import DrawerContextProvider from '../context/drawerContext';
import FileImportProcessContextProvider from '../context/fileImportProcessContext';
import FileImportProvider from '../context/fileImportContext';
import FileImportPermissionProvider from '../context/fileImportPermissionsContext';
import SelectedClientProvider from '../context/selectedClientContext';
import ExportReportContextProvider from '../context/exportReportContext';
import ClientSettingsProvider from '../context/clientSettingsContext';
import InvIneligibleReportProvider from '../context/invIneligibleReportContext';
import { BankLogoContext } from '../context/bankLogoContext';

// Utilities (not lazy-loaded)
import axiosInstance from '../service/axiosInstance';
import { API_DOMAIN, GET, POST, routeList } from '../utility/constants';
import { blobToBase64 } from '../utility/helper';
import { setNewLogoDataUrl } from '../actions/bankLogoActions';

// Layout (usually loaded with app)
import Layout from '../components/layout';

// Lazy-loaded pages
const LandingPage = lazy(() => import('../pages/landing-page'));
const NoLandingPage = lazy(() => import('../pages/no-landing-page'));
const APAgingReport = lazy(() => import('../pages/reports/ap-aging'));
const ARAgingReport = lazy(() => import('../pages/reports/ar-aging'));
const ARIneligibleCompare = lazy(() => import('../pages/reports/ar-compare'));
const ArIneligibleReport = lazy(() => import('../pages/reports/ar-ineligible'));
const BorrowingBaseReport = lazy(() => import('../pages/reports/borrowing-base'));
const ClientSetting = lazy(() => import('../pages/client-setting'));
const Clients = lazy(() => import('../pages/clients'));
const CustomerSetting = lazy(() => import('../pages/customer-setting'));
const ErrorPage = lazy(() => import('../pages/error'));
const FileImport = lazy(() => import('../pages/file-import/landing-page'));
const FileUploadStepper = lazy(() => import('../pages/file-import/stepper'));
const IneligibleSettings = lazy(() => import('../pages/ineligible-settings'));
const InventoryIneligibles = lazy(() => import('../pages/inventory-ineligibles'));
const InventoryWorksheet = lazy(() => import('../pages/inventory-worksheet'));
const LenderSettings = lazy(() => import('../pages/lender-settings'));
const LoanBalances = lazy(() => import('../pages/loan-balances'));
const Reports = lazy(() => import('../pages/reports'));
const Reserve = lazy(() => import('../pages/reserve'));
const ResetPassword = lazy(() => import('../pages/forgot-password/password-reset-request'));
const RollForwardReport = lazy(() => import('../pages/reports/roll-forward'));
const SignIn = lazy(() => import('../pages/sign-in'));
const SignUp = lazy(() => import('../pages/sign-up'));
const UpdatePassword = lazy(() => import('../pages/forgot-password/update-password'));
const UpdateSuccess = lazy(() => import('../pages/forgot-password/update-success'));
const UserAndRoles = lazy(() => import('../pages/users-and-roles'));
const UserProfile = lazy(() => import('../pages/users-profile'));
const ExchangeRate = lazy(() => import('../pages/client-setting/exchange-rate'));
const RequestSuccess = lazy(() => import('../pages/forgot-password/request-success'));
const AuditTrailPage = lazy(() => import('../pages/audit-trail'));
const Analytics = lazy(() => import('../pages/analytics'));
const ForbiddenPage = lazy(() => import('../pages/forbidden'));
const InventoryIneligibleReport = lazy(() => import('../pages/reports/inv-ineligible'));
const DocumentParser = lazy(() => import('../pages/document-parser'));
const CustomReports = lazy(() => import('../pages/custom-reports'));
const CustomerReportsAddTemplate = lazy(() => import('../pages/custom-reports/add-template'));
const CustomerReportsEditTemplate = lazy(() => import('../pages/custom-reports/edit-template'));
const CustomBorrowingBaseReport = lazy(() => import('../pages/custom-reports/borrowing-base'));
const CustomARIneligibleDetailReport = lazy(() => import('../pages/custom-reports/ar-ineligible-detail'));
const CustomConcentrationTop10Report = lazy(() => import('../pages/custom-reports/concentration-top10'));
const CustomHistoricalAgingReport = lazy(() => import('../pages/custom-reports/historical-aging'));
const CustomHistoricalTrendCardReport = lazy(() => import('../pages/custom-reports/historical-trend-card'));

const Router = () => {
  const { state } = useContext(AuthContext);
  const { token } = state;
  const logoDispatch = useContext(BankLogoContext).dispatch;

  useEffect(() => {
    if (token) fetchLenderProfile();
  }, [])

  const fetchLenderProfile = async () => {
    try {
      const response = await axiosInstance.request({
        url: `${API_DOMAIN}/bankDetails/1`,
        method: GET,
    });
    const imageFile = await axiosInstance.request({
      url: `${API_DOMAIN}/bankDetails/logo/download`,
      method: POST,
      responseType: 'blob',
      data: {filename: response.data.bankImageFileName},
      headers: {'content-type': 'multipart/form-data'}
    })
      const logoDataUrl = await blobToBase64(imageFile.data);
      logoDispatch(setNewLogoDataUrl({logoDataUrl}));
    } catch (err) {
      console.log(err);
    }

  }

  return (
    <BrowserRouter>
      <Suspense fallback={<div />}>
        {token ? (
          <SelectedClientProvider>
            <Routes>
              <Route path='/' element={
                <CalcIneligibleContextProvider>
                  <FileImportProcessContextProvider>
                    <DrawerContextProvider>
                      <ExportReportContextProvider>
                        <Layout />
                      </ExportReportContextProvider>
                    </DrawerContextProvider>
                  </FileImportProcessContextProvider>
                </CalcIneligibleContextProvider>
              }>
                <Route index element={<LandingPage />} />
                <Route path='/no-landing-page' element={<NoLandingPage />} />
                <Route path='/sign-in' element={<SignIn />}></Route>
                <Route path='/create-password' element={<UpdatePassword type='create'/>}></Route>
                <Route path='/forgot-password' element={<ResetPassword />}></Route>
                <Route path='/forgot-password/new-password' element={<UpdatePassword type='update'/>}></Route>

                <Route path='/clients'>
                  <Route index element={<Clients />} />
                </Route>

                {/* Start Clients */}
                <Route path='clients'>
                  <Route
                    index
                    element={<Clients />}
                  />
                  <Route
                    path=':borrowerId/settings'
                  >
                    <Route
                      index
                      element={
                        <ClientSettingsProvider>
                          <ClientSetting />
                        </ClientSettingsProvider>
                      }
                    />
                    <Route
                      path='exchangeRate'
                      element={<ExchangeRate/>} 
                    />
                  </Route>
                  <Route
                    path=':borrowerId/settings/:arCollateralId/customers'
                    element={<CustomerSetting />}
                  />
                  <Route
                    path=':borrowerId/settings/:arCollateralId/customers/edit'
                    element={<CustomerSetting />}
                  />
                  <Route
                    path=':borrowerId/settings/:arCollateralId/vendors/edit'
                    element={<CustomerSetting />}
                  />
                  <Route
                    path=':borrowerId/settings/loan-balances'
                    element={<LoanBalances />}
                  />
                  <Route
                    path=':borrowerId/settings/reserves'
                    element={<Reserve />}
                  />
                  <Route
                    path=':borrowerId/settings/:invCollateralFk/inventory-worksheet'
                    element={<InventoryWorksheet path='clients' />}
                  />
                  <Route
                    path=':borrowerId/settings/:invCollateralId/ineligibles-inventory'
                    element={<InventoryIneligibles />}
                  />
                </Route>
                {/* End Clients */}
                <Route path='reports'>
                  <Route index element={<Reports />} />
                  <Route
                    path='borrowing-base'
                    element={<BorrowingBaseReport />}
                  ></Route>
                  <Route
                    path='ar-ineligible'
                    element={<ArIneligibleReport />}
                  />
                  <Route
                    path='ar-aging'
                    element={<ARAgingReport />}
                  ></Route>
                  <Route
                    path='inventory'
                    element={
                      <InvIneligibleReportProvider>
                        <InventoryIneligibleReport />
                      </InvIneligibleReportProvider>
                    }
                  ></Route>
                  <Route
                    path='roll-forward'
                    element={<RollForwardReport />}
                  ></Route>
                  <Route
                    path='ar-compare'
                    element={<ARIneligibleCompare />}
                  />
                  <Route
                    path='ap-aging'
                    element={<APAgingReport />}
                  />
                </Route>
                <Route path='custom-reports'>
                  <Route index element={<CustomReports />} />
                  <Route
                    path='add-template'
                    element={<CustomerReportsAddTemplate />}>
                  </Route>
                  <Route
                    path='edit-template/:templateId'
                    element={<CustomerReportsEditTemplate />}>
                  </Route>
                  <Route
                    path='borrowing-base'
                    element={<CustomBorrowingBaseReport />}>
                  </Route>
                  <Route
                    path='ar-ineligible-detail'
                    element={<CustomARIneligibleDetailReport />}>
                  </Route>
                  <Route
                    path='concentration-top10'
                    element={<CustomConcentrationTop10Report />}>
                  </Route>
                  <Route
                    path='historical-aging'
                    element={<CustomHistoricalAgingReport />}>
                  </Route>
                  <Route
                    path='historical-trend-card'
                    element={<CustomHistoricalTrendCardReport />}>
                  </Route>
                </Route>
                <Route
                  path='/analytics'
                  element={<Analytics />}
                ></Route>
                {/* <Route
                  path='/document-parser'
                  element={<DocumentParser />}
                ></Route> */}
                <Route
                  path='/general-settings'
                  element={<LenderSettings />}
                ></Route>
                <Route
                  path='/audit-trail'
                  element={<AuditTrailPage />}
                ></Route>
                {/* users and roles */}
                <Route
                  path='/users-and-roles'
                >
                  <Route 
                    index
                    element={<UserAndRoles />}
                  />
                  <Route
                  path=':id/users-profile'
                  element={<UserProfile />}
                  />
                </Route>
                {/* end users and role */}
                <Route
                  path='/file-import'
                  element={
                    <FileImportPermissionProvider>
                      <FileImportProvider>
                        <FileImport/>
                      </FileImportProvider>
                    </FileImportPermissionProvider>
                  }
                >
                </Route>
                <Route path='/ineligible-settings' element={<IneligibleSettings/>}></Route>
                <Route
                  path='/upload-files'
                  element={
                    <FileImportProvider>
                      <FileUploadStepper />
                    </FileImportProvider>
                  }
                >
                </Route>
              </Route>
              <Route path='/forbidden' element={<ForbiddenPage />} />
              <Route path='*' element={<ErrorPage />} />
            </Routes>
          </SelectedClientProvider>
        ) : (
          <Routes>
            <Route path='/' element={<SignIn />}></Route>
            <Route path='/sign-in' element={<SignIn />}></Route>
            <Route path='/sign-up' element={<SignUp />}></Route>
            <Route path='/forgot-password' element={<ResetPassword />}></Route>
            <Route path='/forgot-password/request-success' element={<RequestSuccess />}></Route>
            <Route path='/forgot-password/new-password' element={<UpdatePassword type='update'/>}></Route>
            <Route path='/forgot-password/success' element={<UpdateSuccess type='update'/>}></Route>
            <Route path='/create-password' element={<UpdatePassword type='create'/>}></Route>
            <Route path='/create-password/success' element={<UpdateSuccess type='create'/>}></Route>

            {routeList.map(path => <Route key={path} path={path} element={<ForbiddenPage />} />)}

            <Route path='*' element={<ErrorPage />} />
          </Routes>
        )}
      </Suspense>
    </BrowserRouter>
  );
};

export default Router;
